<template>
    <kt-modal
        :visible="visible"
        title="Finalize Work Order"
        :size="'modal-xl'"
        @dismiss="handleCancel"
    >
        <template #body>
            <div class="finalize-form">
                <!-- Invoice Header Section -->
                <div class="row mb-3">
                    <div class="col-md-4">
                        <kt-input
                            label="Invoice Number"
                            v-model="formData.InvoiceNumber"
                            input-class="form-control form-control-sm"
                        ></kt-input>
                    </div>
                    <div class="col-md-4">
                        <kt-select
                            label="Type"
                            v-model="formData.Title"
                            :options="titleOptions"
                        ></kt-select>
                    </div>
                    <div class="col-md-4">
                        <kt-input
                            type="date"
                            label="Date"
                            v-model="formData.DateCreated"
                            input-class="form-control form-control-sm"
                        ></kt-input>
                    </div>
                </div>

                <!-- Client Information -->
                <div class="row mb-3">
                    <div class="col-md-12">
                        <h6><strong>Client Information</strong></h6>
                        <div v-if="workOrder.Contact">
                            <p class="mb-1"><strong>{{ workOrder.Contact.Company || workOrder.Contact.LastName }}</strong></p>
                            <p class="mb-0 text-muted small">{{ workOrder.Contact.Street }}</p>
                            <p class="mb-0 text-muted small">{{ workOrder.Contact.City }}, {{ workOrder.Contact.State }} {{ workOrder.Contact.Zip }}</p>
                        </div>
                    </div>
                </div>

                <!-- Line Items Table -->
                <div class="row mb-3">
                    <div class="col-md-12">
                        <div class="d-flex justify-content-between align-items-center mb-2">
                            <h6><strong>Line Items</strong></h6>
                            <button 
                                type="button" 
                                class="btn btn-sm btn-outline-primary"
                                @click="addLineItem"
                            >
                                <i class="fa fa-plus"></i> Add Item
                            </button>
                        </div>
                        
                        <div class="table-responsive">
                            <table class="table table-sm table-bordered">
                                <thead class="table-light">
                                    <tr>
                                        <th style="width: 40px"></th>
                                        <th style="width: 300px">Description</th>
                                        <th style="width: 100px">Quantity</th>
                                        <th style="width: 120px">Unit Cost</th>
                                        <th style="width: 80px">Taxable</th>
                                        <th style="width: 120px">Line Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(item, index) in formData.Items" :key="index">
                                        <td>
                                            <button 
                                                type="button"
                                                class="btn btn-sm btn-outline-danger"
                                                @click="removeLineItem(index)"
                                                title="Remove"
                                            >
                                                <i class="fa fa-trash"></i>
                                            </button>
                                        </td>
                                        <td>
                                            <input 
                                                type="text" 
                                                class="form-control form-control-sm"
                                                v-model="item.Description"
                                                placeholder="Item description"
                                            />
                                        </td>
                                        <td>
                                            <input 
                                                type="number" 
                                                class="form-control form-control-sm"
                                                v-model.number="item.Quantity"
                                                @input="calculateLineTotal(item)"
                                                min="0"
                                                step="1"
                                            />
                                        </td>
                                        <td>
                                            <input 
                                                type="number" 
                                                class="form-control form-control-sm"
                                                v-model.number="item.UnitCost"
                                                @input="calculateLineTotal(item)"
                                                min="0"
                                                step="0.01"
                                            />
                                        </td>
                                        <td class="text-center">
                                            <input 
                                                type="checkbox" 
                                                class="form-check-input"
                                                v-model="item.Taxable"
                                                @change="calculateTotals"
                                            />
                                        </td>
                                        <td class="text-end">
                                            <strong>${{ item.LineTotal?.toFixed(2) || '0.00' }}</strong>
                                        </td>
                                    </tr>
                                    <tr v-if="formData.Items.length === 0">
                                        <td colspan="6" class="text-center text-muted">
                                            No line items. Click "Add Item" to add one.
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <!-- Totals Section -->
                <div class="row mb-3">
                    <div class="col-md-8"></div>
                    <div class="col-md-4">
                        <table class="table table-sm">
                            <tbody>
                                <tr>
                                    <td><strong>Subtotal:</strong></td>
                                    <td class="text-end">${{ totals.subtotal.toFixed(2) }}</td>
                                </tr>
                                <tr>
                                    <td>
                                        <strong>Tax:</strong>
                                        <input 
                                            type="number"
                                            class="form-control form-control-sm d-inline-block ms-2"
                                            style="width: 80px;"
                                            v-model.number="formData.TaxRate"
                                            @input="calculateTotals"
                                            min="0"
                                            step="0.01"
                                        />%
                                    </td>
                                    <td class="text-end">${{ totals.tax.toFixed(2) }}</td>
                                </tr>
                                <tr>
                                    <td><strong>Total:</strong></td>
                                    <td class="text-end"><strong>${{ totals.total.toFixed(2) }}</strong></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- Terms & Notes -->
                <div class="row">
                    <div class="col-md-6">
                        <label><strong>Terms & Conditions</strong></label>
                        <textarea 
                            class="form-control form-control-sm" 
                            v-model="formData.Terms"
                            rows="4"
                        ></textarea>
                    </div>
                    <div class="col-md-6">
                        <label><strong>Special Instructions</strong></label>
                        <textarea 
                            class="form-control form-control-sm" 
                            v-model="formData.SpecialInstructions"
                            rows="4"
                        ></textarea>
                    </div>
                </div>
            </div>
        </template>
        
        <template #footer>
            <div class="kt-modal-actions">
                <button class="btn btn-outline-secondary" @click="handleCancel" :disabled="isProcessing">
                    <X :size="16" class="me-1" />Cancel
                </button>
                <button class="btn btn-primary" @click="handleFinalize" :disabled="isProcessing">
                    <span v-if="isProcessing">
                        <Loader2 :size="16" class="me-1 spin" /> Finalizing...
                    </span>
                    <span v-else>
                        <Check :size="16" class="me-1" /> Finalize
                    </span>
                </button>
            </div>
        </template>
    </kt-modal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { WorkOrderModel } from '@/models/WorkOrderModel';
import { WorkOrderItemModel } from '@/models/WorkOrderItemModel';
import KtModal from '@/components/UI/KtModal.vue';
import KtInput from '@/components/UI/KtInput.vue';
import KtSelect from '@/components/UI/KtSelect.vue';
import InvoicePdfService from '@/services/InvoicePdfService';
import Api from '@/services/ApiService';
import { X, Check, Loader2 } from 'lucide-vue-next';
import Alert from '@/services/Alert';
import Toast from '@/services/Toast';
import Helper from '@/services/Helper';

const props = defineProps<{
    visible: boolean;
    workOrder: WorkOrderModel;
}>();

const emit = defineEmits<{
    close: [];
    finalized: [];
}>();

const isProcessing = ref(false);

// Create editable form data from workOrder
const formData = ref<any>({
    InvoiceNumber: '',
    Title: 'Invoice',
    DateCreated: '',
    Terms: '',
    SpecialInstructions: '',
    TaxRate: 0,
    Items: []
});

const titleOptions = [
    { label: 'Invoice', value: 'Invoice' },
    { label: 'Partial', value: 'Partial' },
    { label: 'Estimate', value: 'Estimate' },
    { label: 'Request', value: 'Request' }
];

// Initialize form data when workOrder changes
watch(() => props.workOrder, (newWorkOrder) => {
    if (newWorkOrder && props.visible) {
        formData.value = {
            InvoiceNumber: newWorkOrder.InvoiceNumber || '',
            Title: newWorkOrder.Title || 'Invoice',
            DateCreated: newWorkOrder.DateCreated || Helper.dateToInput(new Date()),
            Terms: newWorkOrder.Terms || '',
            SpecialInstructions: newWorkOrder.SpecialInstructions || '',
            TaxRate: 0, // Default tax rate, can be edited
            Items: newWorkOrder.Items ? newWorkOrder.Items.map(item => ({
                Id: item.Id,
                Description: item.Description || '',
                Quantity: item.Quantity || 1,
                UnitCost: item.UnitCost || 0,
                Taxable: item.Taxable !== false,
                LineTotal: item.LineTotal || ((item.Quantity || 0) * (item.UnitCost || 0))
            })) : []
        };
        calculateTotals();
    }
}, { immediate: true });

// Totals calculation
const totals = ref({
    subtotal: 0,
    tax: 0,
    total: 0
});

const calculateLineTotal = (item: any) => {
    item.LineTotal = (item.Quantity || 0) * (item.UnitCost || 0);
    calculateTotals();
};

const calculateTotals = () => {
    const subtotal = formData.value.Items.reduce((sum: number, item: any) => {
        return sum + (item.LineTotal || 0);
    }, 0);
    
    const taxableAmount = formData.value.Items
        .filter((item: any) => item.Taxable)
        .reduce((sum: number, item: any) => sum + (item.LineTotal || 0), 0);
    
    const tax = taxableAmount * ((formData.value.TaxRate || 0) / 100);
    
    totals.value = {
        subtotal,
        tax,
        total: subtotal + tax
    };
};

const addLineItem = () => {
    formData.value.Items.push({
        Description: '',
        Quantity: 1,
        UnitCost: 0,
        Taxable: true,
        LineTotal: 0
    });
};

const removeLineItem = (index: number) => {
    formData.value.Items.splice(index, 1);
    calculateTotals();
};

const handleCancel = () => {
    if (!isProcessing.value) {
        emit('close');
    }
};

const handleFinalize = async () => {
    if (isProcessing.value) return;
    
    try {
        isProcessing.value = true;
        
        // Create a WorkOrderModel from formData for PDF generation
        const pdfWorkOrder = new WorkOrderModel({
            ...props.workOrder,
            InvoiceNumber: formData.value.InvoiceNumber,
            Title: formData.value.Title,
            DateCreated: formData.value.DateCreated,
            Terms: formData.value.Terms,
            SpecialInstructions: formData.value.SpecialInstructions,
            Subtotal: totals.value.subtotal,
            Tax: totals.value.tax,
            Total: totals.value.total,
            Items: formData.value.Items.map((item: any) => new WorkOrderItemModel({
                ...item,
                WorkOrderId: props.workOrder.Id
            }))
        });
        
        // Generate PDF and get blob
        const pdfBlob = await InvoicePdfService.generateInvoicePDFBlob(pdfWorkOrder);
        
        // Create filename
        const today = new Date();
        const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        const title = formData.value.Title || 'Invoice';
        const invoiceNum = formData.value.InvoiceNumber || props.workOrder.Id || 'New';
        const filename = `${title}_${invoiceNum}_${dateStr}.pdf`;
        
        // Convert blob to File for upload
        const pdfFile = new File([pdfBlob], filename, { type: 'application/pdf' });
        
        // Upload PDF with "finalized" marker in Extra field
        await Api.uploadWorkOrderFile(pdfFile, props.workOrder.Id!, 'finalized');
        
        // Update WorkOrder status to Finalized (5) and DateCompleted
        const updatedWorkOrder = new WorkOrderModel({
            ...props.workOrder,
            Status: 5, // Finalized
            DateCompleted: Helper.dateToInput(new Date()),
            InvoiceNumber: formData.value.InvoiceNumber,
            Title: formData.value.Title,
            Terms: formData.value.Terms,
            SpecialInstructions: formData.value.SpecialInstructions,
            Subtotal: totals.value.subtotal,
            Tax: totals.value.tax,
            Total: totals.value.total
        });
        
        await Api.saveWorkOrder(updatedWorkOrder);
        
        Toast.success('Work order finalized successfully!');
        emit('finalized');
        emit('close');
        
    } catch (error) {
        console.error('Error finalizing work order:', error);
        Alert.notify({ text: 'Failed to finalize work order', type: 'error' });
    } finally {
        isProcessing.value = false;
    }
};
</script>

<style scoped>
.finalize-form {
    height: calc(100vh - 180px);
    overflow-y: auto;
}

.table-responsive {
    max-height: calc(100vh - 500px);
    overflow-y: auto;
}

input[type="number"].form-control-sm {
    text-align: right;
}
</style>
